import { useEffect, useRef, useState, useCallback } from "react";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import L from "leaflet";
import "leaflet-draw";
import { api, formatApiErrorDetail } from "@/api";
import { confirmDialog } from "@/components/Confirm";
import { toast } from "sonner";
import { GripVertical, MapPin, Trash2, Check, X } from "lucide-react";
import { MapShell } from "@/components/MapShell";
import { kenmerkMeta } from "@/lib/kenmerkIcons";
import { makeBaseLayer } from "@/lib/mapBase";

export function BuildingsMapEditor({ canWrite, onChanged }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const drawnRef = useRef(null);
  const baseRef = useRef(null);
  const [buildings, setBuildings] = useState([]);
  const [area, setArea] = useState(null);
  const [ready, setReady] = useState(false);
  const [q, setQ] = useState("");
  const [mapCfg, setMapCfg] = useState(null);
  const [sat, setSat] = useState(false);
  const [pickerAt, setPickerAt] = useState(null);
  const [pq, setPq] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const loadData = useCallback(async () => {
    const [b, s, m] = await Promise.all([api.get("/buildings"), api.get("/settings/service-area"), api.get("/settings/map")]);
    setBuildings(b.data); setArea(s.data.polygon || null); setMapCfg(m.data); setReady(true);
  }, []);
  useEffect(() => { loadData(); }, [loadData]);

  const persist = useCallback(async (b, lat, lng) => {
    try {
      await api.put(`/buildings/${b.id}/position`, { lat: Number(lat.toFixed(6)), lng: Number(lng.toFixed(6)) });
      setBuildings((prev) => prev.map((x) => (x.id === b.id ? { ...x, lat, lng } : x)));
      toast.success(`${b.name} geplaatst`);
      onChanged && onChanged();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }, [onChanged]);

  const persistArea = useCallback(async (polygon) => {
    try { await api.put("/settings/service-area", { polygon }); toast.success("Verzorgingsgebied opgeslagen"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }, []);

  // Init map once
  useEffect(() => {
    if (!ready || !mapEl.current || mapRef.current) return;
    const center = buildings[0] ? [buildings[0].lat, buildings[0].lng] : [52.11, 5.18];
    const map = L.map(mapEl.current, { zoomControl: true, attributionControl: false }).setView(center, 14);
    mapRef.current = map;
    baseRef.current = makeBaseLayer(mapCfg, false).addTo(map);
    if (area && area.length >= 3) { try { map.fitBounds(L.polygon(area).getBounds().pad(0.15)); } catch (e) {} }
    else if (buildings.length) { try { map.fitBounds(L.featureGroup(buildings.map((b) => L.marker([b.lat, b.lng]))).getBounds().pad(0.3)); } catch (e) {} }
    setTimeout(() => map.invalidateSize(), 300);

    const drawn = new L.FeatureGroup();
    map.addLayer(drawn);
    drawnRef.current = drawn;

    if (canWrite) {
      const dc = new L.Control.Draw({
        position: "topright",
        draw: { polygon: { showArea: false, shapeOptions: { color: "#2563eb", weight: 2, fillOpacity: 0.1 } }, rectangle: { showArea: false, shapeOptions: { color: "#2563eb", weight: 2, fillOpacity: 0.1 } }, polyline: false, circle: false, marker: false, circlemarker: false },
        edit: { featureGroup: drawn, remove: false },
      });
      map.addControl(dc);
      const coordsOf = (layer) => layer.getLatLngs()[0].map((p) => [Number(p.lat.toFixed(6)), Number(p.lng.toFixed(6))]);
      map.on(L.Draw.Event.CREATED, (e) => { const c = coordsOf(e.layer); setArea(c); persistArea(c); });
      map.on(L.Draw.Event.EDITED, (e) => { let c = null; e.layers.eachLayer((l) => { c = coordsOf(l); }); if (c) { setArea(c); persistArea(c); } });
    }

    // Klik-en-houd (desktop) of rechtsklik / long-press (tablet) op de kaart => kies een gebouw voor die plek
    let pressTimer = null; let downLatLng = null;
    const openAt = (latlng) => { if (!canWrite) return; setPq(""); setPickerAt({ lat: Number(latlng.lat.toFixed(6)), lng: Number(latlng.lng.toFixed(6)) }); };
    map.on("mousedown", (e) => { downLatLng = e.latlng; clearTimeout(pressTimer); pressTimer = setTimeout(() => openAt(downLatLng), 500); });
    map.on("mouseup", () => clearTimeout(pressTimer));
    map.on("movestart", () => clearTimeout(pressTimer));
    map.on("contextmenu", (e) => { try { L.DomEvent.preventDefault(e.originalEvent); } catch (_) {} openAt(e.latlng); });
  }, [ready, buildings, canWrite, persistArea, area]);

  // Swap base layer when config loads or satellite toggles
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    if (baseRef.current) map.removeLayer(baseRef.current);
    baseRef.current = makeBaseLayer(mapCfg, sat).addTo(map);
    baseRef.current.bringToBack();
    if (!sat && mapCfg && mapCfg.mode === "static" && mapCfg.bounds) { try { map.fitBounds(mapCfg.bounds); } catch (e) {} }
  }, [mapCfg, sat]);

  // Sync markers with buildings
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const seen = new Set();
    buildings.forEach((b) => {
      seen.add(b.id);
      const sel = b.id === selectedId;
      const icon = L.divIcon({ className: "", html: `<div style="background:${sel ? "#f59e0b" : "#1e40af"};color:#fff;border:2px solid #fff;border-radius:9px;padding:4px 8px;font-size:11px;font-weight:700;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,.35)${sel ? ";outline:3px solid #fde68a;transform:scale(1.15)" : ""}">${b.code || "•"}</div>`, iconAnchor: [16, 16] });
      let m = markersRef.current[b.id];
      if (m) { m.setLatLng([b.lat, b.lng]); m.setIcon(icon); if (sel) m.setZIndexOffset(1000); else m.setZIndexOffset(0); }
      else {
        const ken = (b.kenmerken || []).map((k) => `${kenmerkMeta(k).emoji} ${k}`).join("<br/>");
        m = L.marker([b.lat, b.lng], { icon, draggable: canWrite }).addTo(map).bindPopup(`<b>${b.name}</b>${ken ? "<br/>" + ken : ""}`);
        if (canWrite) m.on("dragend", (e) => { const { lat, lng } = e.target.getLatLng(); persist(b, lat, lng); });
        markersRef.current[b.id] = m;
      }
    });
    Object.keys(markersRef.current).forEach((id) => {
      if (!seen.has(id)) { map.removeLayer(markersRef.current[id]); delete markersRef.current[id]; }
    });
  }, [buildings, canWrite, persist, selectedId]);

  // Draw service area
  useEffect(() => {
    const map = mapRef.current, drawn = drawnRef.current;
    if (!map || !drawn) return;
    drawn.clearLayers();
    if (area && area.length >= 3) drawn.addLayer(L.polygon(area, { color: "#2563eb", weight: 2, fillColor: "#2563eb", fillOpacity: 0.1 }));
  }, [area]);

  const onDrop = (e) => {
    e.preventDefault();
    if (!canWrite) return;
    const bid = e.dataTransfer.getData("bid");
    const b = buildings.find((x) => x.id === bid);
    if (!b || !mapRef.current) return;
    const rect = mapEl.current.getBoundingClientRect();
    const latlng = mapRef.current.containerPointToLatLng(L.point(e.clientX - rect.left, e.clientY - rect.top));
    persist(b, latlng.lat, latlng.lng);
  };

  const clearArea = async () => { if (!(await confirmDialog({ title: "Verzorgingsgebied wissen", message: "Weet je zeker dat je het verzorgingsgebied wilt wissen?", danger: true, confirmText: "Wissen" }))) return; setArea(null); await persistArea([]); };

  const choosePlace = (b) => { if (!pickerAt) return; const { lat, lng } = pickerAt; setPickerAt(null); setPq(""); setSelectedId(b.id); persist(b, lat, lng); if (mapRef.current) mapRef.current.setView([lat, lng]); };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="buildings-map-editor">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-bold text-slate-900">Plattegrond & verzorgingsgebied</div>
        {canWrite && (
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span>Sleep een gebouw uit de lijst op de kaart, of <b>klik-en-houd</b> (rechtsklik / long-press) op de kaart om een gebouw te kiezen · versleep markers · teken gebied rechtsboven</span>
            {area && <button data-testid="clear-area-btn" onClick={clearArea} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 font-semibold text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /> Gebied wissen</button>}
          </div>
        )}
      </div>
      <div className="grid gap-4 lg:grid-cols-4">
        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Gebouwen ({buildings.length})</div>
          <input data-testid="map-building-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek naam/functie…"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none focus:border-blue-500" />
          <div className="max-h-[400px] space-y-2 overflow-y-auto pr-1">
          {buildings.filter((b) => !q || (`${b.name} ${b.function || ""}`).toLowerCase().includes(q.toLowerCase())).map((b) => (
            <div key={b.id} data-testid={`drag-building-${b.id}`}
              draggable={canWrite}
              onDragStart={(e) => e.dataTransfer.setData("bid", b.id)}
              className={`flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2.5 ${canWrite ? "cursor-grab active:cursor-grabbing" : ""}`}>
              {canWrite && <GripVertical className="h-4 w-4 shrink-0 text-slate-400" />}
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-xs font-bold text-white">{b.code}</div>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-slate-900">{b.name}</div>
                {b.function && <div className="truncate text-[11px] font-medium text-blue-600">{b.function}</div>}
                <div className="truncate text-[11px] text-slate-400">{Number(b.lat).toFixed(4)}, {Number(b.lng).toFixed(4)}</div>
              </div>
            </div>
          ))}
          </div>
        </div>
        <div className="lg:col-span-3">
          <MapShell heightClass="h-[460px]" testid="beheer-map" onToggle={() => setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 80)}>
            <button data-testid="beheer-satellite-toggle" onClick={() => setSat((s) => !s)}
              className={`absolute left-3 top-3 z-[1001] rounded-lg border px-3 py-1.5 text-xs font-semibold shadow ${sat ? "border-blue-500 bg-blue-600 text-white" : "border-slate-200 bg-white/95 text-slate-700 hover:bg-white"}`}>
              {sat ? "Satelliet aan" : "Satelliet"}
            </button>
            <div ref={mapEl} data-testid="beheer-map" onDragOver={(e) => e.preventDefault()} onDrop={onDrop}
              className="h-full w-full overflow-hidden rounded-xl border border-slate-200" />
          </MapShell>
        </div>
      </div>

      {pickerAt && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 p-4" onClick={() => setPickerAt(null)} data-testid="building-picker">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl">
            <div className="mb-1 flex items-center justify-between">
              <div className="text-sm font-bold text-slate-900">Gebouw plaatsen op deze locatie</div>
              <button data-testid="picker-close" onClick={() => setPickerAt(null)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="mb-2 text-[11px] text-slate-400">{pickerAt.lat.toFixed(5)}, {pickerAt.lng.toFixed(5)} · <span className="inline-flex items-center gap-1"><span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-emerald-500 text-white"><Check className="h-2.5 w-2.5" /></span> staat al op de kaart</span></div>
            <input autoFocus data-testid="picker-search" value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Zoek gebouw…"
              className="mb-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
            <div className="max-h-[50vh] space-y-1 overflow-y-auto pr-1">
              {buildings.filter((b) => !pq || (`${b.name} ${b.code || ""} ${b.function || ""}`).toLowerCase().includes(pq.toLowerCase())).map((b) => {
                const placed = b.lat != null && b.lng != null;
                return (
                  <button key={b.id} data-testid={`picker-item-${b.id}`} onClick={() => choosePlace(b)}
                    className="flex w-full items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-left transition-colors hover:bg-blue-50">
                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${placed ? "bg-emerald-500 text-white" : "border border-slate-300 text-transparent"}`}><Check className="h-3.5 w-3.5" /></span>
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-[10px] font-bold text-white">{b.code}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-slate-900">{b.name}</span>
                      {b.function && <span className="block truncate text-[11px] font-medium text-blue-600">{b.function}</span>}
                      {placed && <span className="block text-[10px] text-emerald-600">Al op de kaart · klik om hierheen te verplaatsen</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
